import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { hiringReferralsApiService } from '../../services/apiServices';
import {
  ReferralApplicationResponse,
  ReferralApplicationStatus,
  ResumeType,
} from '../../api-client';
import { getReferralApplicationStatusLabel } from '../utils/statusConverters';

const MyHiringReferralsDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<ReferralApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) {
      navigate('/hiring/my');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await hiringReferralsApiService.getMyApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке моей заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const getStatusBadgeVariant = (status: ReferralApplicationStatus) => {
    switch (status) {
      case ReferralApplicationStatus.PENDING:
        return 'warning';
      case ReferralApplicationStatus.REVIEWING:
        return 'info';
      case ReferralApplicationStatus.ACCEPTED:
        return 'success';
      case ReferralApplicationStatus.REJECTED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleDownloadResume = async () => {
    if (!application || !applicationId) return;

    try {
      setDownloadingFile(true);
      setFileError(null);

      const response = await hiringReferralsApiService.getApplicationFileUrl(applicationId);
      const fileUrl = response.data?.resume_file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на файл');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Ошибка при скачивании резюме:', err);
      setFileError(err.message || 'Ошибка при скачивании резюме');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleOpenResumeUrl = () => {
    if (!application?.resume_url) return;
    window.open(application.resume_url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/hiring/my')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/hiring/my">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {fileError && <Alert variant="warning">{fileError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getStatusBadgeVariant(application.status)}>
                {getReferralApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>ID заявки</th>
                    <td>{application.id}</td>
                  </tr>
                  <tr>
                    <th>Кандидат</th>
                    <td>{application.candidate_name || '—'}</td>
                  </tr>
                  <tr>
                    <th>Направление</th>
                    <td>{application.direction?.name || '—'}</td>
                  </tr>
                  <tr>
                    <th>Статус</th>
                    <td>
                      <Badge bg={getStatusBadgeVariant(application.status)}>
                        {getReferralApplicationStatusLabel(application.status)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Тип резюме</th>
                    <td>
                      <Badge bg={application.resume_type === ResumeType.FILE ? 'success' : 'info'}>
                        {application.resume_type === 'file' ? 'Файл' : 'URL'}
                      </Badge>
                    </td>
                  </tr>
                  {application.resume_type === ResumeType.URL && application.resume_url && (
                    <tr>
                      <th>Ссылка на резюме</th>
                      <td>
                        <Button variant="primary" onClick={handleOpenResumeUrl} size="sm">
                          Открыть резюме в новой вкладке
                        </Button>
                      </td>
                    </tr>
                  )}
                  {application.resume_type === ResumeType.FILE && (
                    <tr>
                      <th>Файл резюме</th>
                      <td>
                        <Button
                          variant="primary"
                          onClick={handleDownloadResume}
                          disabled={downloadingFile}
                          size="sm"
                        >
                          {downloadingFile ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Скачивание...
                            </>
                          ) : (
                            'Скачать резюме'
                          )}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {application.comment && (
                    <tr>
                      <th>Комментарий</th>
                      <td>{application.comment}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Дата создания</th>
                    <td>{new Date(application.created_at).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MyHiringReferralsDetailPage;
